import { useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, Plus, Check, Trash2 } from "lucide-react";
import { LQDua } from "@/hooks/useLaylatulQadr";

interface DuaAmplifierProps {
  nightNumber: number;
  duas: LQDua[];
  onAddDua: (text: string) => Promise<void>;
  onMarkMade: (duaId: string, nightNumber: number) => Promise<void>;
  onDelete: (duaId: string) => Promise<void>;
}

export const DuaAmplifier = ({ nightNumber, duas, onAddDua, onMarkMade, onDelete }: DuaAmplifierProps) => {
  const [newDuaText, setNewDuaText] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const handleAdd = async () => {
    if (!newDuaText.trim()) return;
    await onAddDua(newDuaText.trim());
    setNewDuaText("");
    setIsAdding(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.2 }}
      className="space-y-4"
    >
      <h2 className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-3">
        <BookOpen className="w-4 h-4" />
        Dua Amplifier
      </h2>
      <p className="text-cream/50 text-xs">
        Build your personal dua list. Mark each dua when you've made it tonight.
      </p>

      <div className="space-y-3">
        {duas.map((dua) => {
          const madeTonight = dua.nights_made.includes(nightNumber);
          const nightCount = dua.nights_made.length;

          return (
            <motion.div
              key={dua.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-[#0f0f23]/50 rounded-sm p-5 border border-gold/10 space-y-2"
            >
              <div className="flex items-start justify-between gap-3">
                <p className="text-cream/85 text-sm flex-1 leading-relaxed">{dua.dua_text}</p>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => dua.id && onMarkMade(dua.id, nightNumber)}
                    className={`p-2 rounded-sm transition-colors ${
                      madeTonight
                        ? "bg-gold/20 text-gold"
                        : "text-cream/30 hover:text-gold/60 hover:bg-gold/5"
                    }`}
                    title={madeTonight ? "Made tonight ✓" : "Mark as made tonight"}
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => dua.id && onDelete(dua.id)}
                    className="p-2 text-cream/20 hover:text-red-400/60 transition-colors rounded-sm"
                    title="Remove dua"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
              {nightCount > 0 && (
                <p className="text-gold/50 text-xs">
                  Made on {nightCount} night{nightCount !== 1 ? "s" : ""}
                </p>
              )}
            </motion.div>
          );
        })}

        {isAdding ? (
          <div className="bg-[#0f0f23]/50 rounded-sm p-5 border border-gold/20 space-y-3">
            <textarea
              value={newDuaText}
              onChange={(e) => setNewDuaText(e.target.value)}
              placeholder="Write your dua..."
              className="w-full bg-transparent border border-gold/15 rounded-sm p-3 text-cream/90 text-sm placeholder:text-cream/30 focus:outline-none focus:border-gold/30 resize-none"
              rows={3}
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={handleAdd}
                disabled={!newDuaText.trim()}
                className="px-5 py-2 border border-gold/50 text-gold text-xs uppercase tracking-wider hover:bg-gold/10 transition-colors rounded-sm disabled:opacity-40"
              >
                Save Dua
              </button>
              <button
                onClick={() => { setIsAdding(false); setNewDuaText(""); }}
                className="px-5 py-2 text-cream/40 text-xs uppercase tracking-wider hover:text-cream/60 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsAdding(true)}
            className="w-full py-4 border border-dashed border-gold/20 rounded-sm text-gold/50 text-xs uppercase tracking-wider hover:border-gold/40 hover:text-gold/70 transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-3 h-3" />
            Add a personal dua
          </button>
        )}
      </div>
    </motion.div>
  );
};
